import {
  DialogType,
  type IBreadcrumbItem,
  type IColumn,
  type IContextualMenuItem,
  type IDropdownOption,
  type IStyle,
  MessageBarType,
} from '@fluentui/react';
import {
  BaseDialog,
  ClassNamesFromIStyles,
  Dropdown,
  dropdownStylesInlineLeft,
  IH2OTheme,
  Info,
  Loader,
  TextWithCopy,
  textWithCopyStylesBorder,
  useClassNames,
  useHaicPageTitle,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useUsers } from '../../authz/providers/UsersProvider';
import {
  ButtonFailed,
  ButtonPaused,
  ButtonPausing,
  ButtonReady,
  ButtonStarting,
} from '../../components/ButtonStartPause/ButtonStartPause';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { HeaderPure } from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import { RowHeaderTitle } from '../../components/RowHeaderTitle/RowHeaderTitle';
import WidgetList from '../../components/WidgetList/WidgetList';
import { useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { DeploymentFixed } from '../../mlops/api';
import { deploymentName } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import {
  DeploymentStatus,
  DeploymentStatus_DeploymentState,
} from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import { useMLOpsService } from '../../mlops/hooks';
import {
  getDeploymentMetadataQueryOptions,
  getDeploymentStatusQueryOptions,
  useMutationDeleteDeployment,
  useMutationRetryDeployment,
  usePauseResumeDeployment,
  useQueryDeployments,
  useQueryDeploymentStatus,
  useQueryEndpoints,
} from '../../mlops/mlops.deployer.hooks';
import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { useCloudPlatformDiscovery, useEnv } from '../../utils/hooks';
import { formatDefaultDateAndTime, formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { StatusIndicator, Tag } from '../Orchestrator/WorkflowTabExecutions';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ROUTES } from './constants';
import { downloadFileFromGoogleByteStream } from './DeploymentTabDetail';
import { ListItemDescription } from './Models';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import { type GranularMLOpsPermissions, useGranularMLOpsPermissions } from './userActions';

export type DeploymentItem = DeploymentFixed & {
  actionsDisabled?: boolean;
  createdByName: string;
  id: string;
  status: DeploymentStatus;
  type: string;
  workspaceId: string;
};

interface IDeploymentsStyles {
  showAllDeployments: IStyle;
  noPermissionsInfo: IStyle;
}

export const MLOPS_NO_ITEMS_BANNER_WIDTH = 600;

const deploymentsStyles = (theme: IH2OTheme): IDeploymentsStyles => {
  return {
    showAllDeployments: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px 0',
      borderWidth: 1,
      borderTopStyle: 'solid',
      borderColor: theme.semanticColors?.inputBorder,
      borderRadius: 4,
      // TODO: Use theme colors.
      backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
      '&:hover': {
        borderColor: theme.semanticColors?.buttonPrimaryBackground,
        cursor: 'pointer',
        button: {
          textDecoration: 'none',
        },
        'button:active': {
          textDecoration: 'none',
        },
      },
      span: {
        color: theme.semanticColors?.textSecondary,
      },
      '&:hover span': {
        color: theme.semanticColors?.textPrimary,
      },
    },
    noPermissionsInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: theme.semanticColors?.textSecondary,
    },
  };
};

export const getDeploymentTypeDisplayName = (item: DeploymentFixed) => {
    if (item?.single) return 'Single model';
    if (item?.shadow) return 'Champion/Challenger';
    if (item?.split) return 'A/B Test';
    return 'Single model';
  },
  // TODO: Find out how to theme this.
  getStateProps = (state?: DeploymentStatus_DeploymentState) => {
    switch (state) {
      case DeploymentStatus_DeploymentState.LAUNCHING:
        return { name: 'Launching', color: 'var(--h2o-yellow500)' };
      case DeploymentStatus_DeploymentState.DEPLOYED:
        return { name: 'Deployed', color: 'var(--h2o-yellow500)' };
      case DeploymentStatus_DeploymentState.FAILED:
        return { name: 'Failed', color: 'var(--h2o-red500)' };
      case DeploymentStatus_DeploymentState.HEALTHY:
        return { name: 'Healthy', color: 'var(--h2o-green500)' };
      case DeploymentStatus_DeploymentState.UNHEALTHY:
        return { name: 'Unhealthy', color: 'var(--h2o-red500)' };
      case DeploymentStatus_DeploymentState.TERMINATING:
        return { name: 'Terminating', color: 'var(--h2o-yellow500)' };
      case DeploymentStatus_DeploymentState.PENDING:
        return { name: 'Pending', color: 'var(--h2o-yellow500)' };
      case DeploymentStatus_DeploymentState.TERMINATED:
        return { name: 'Terminated', color: 'var(--h2o-red500)' };
      case DeploymentStatus_DeploymentState.PREPARING:
        return { name: 'Preparing', color: 'var(--h2o-yellow500)' };
      case DeploymentStatus_DeploymentState.STOPPED:
        return { name: 'Stopped', color: 'var(--h2o-gray900)' };
      case DeploymentStatus_DeploymentState.DEPLOYMENT_STATE_UNSPECIFIED:
        return { name: 'Unspecified', color: 'var(--h2o-gray900)' };
      default:
        return { name: 'Unknown', color: 'var(--h2o-gray900)' };
    }
  };

export const DeploymentStatusWidget = ({
  deploymentId,
  workspaceId,
  initialStatus,
  showTooltip = true,
}: {
  deploymentId: string;
  initialStatus: DeploymentStatus;
  showTooltip?: boolean;
  workspaceId: string;
}) => {
  const deploymentStatusQuery = useQueryDeploymentStatus(
    { deploymentId, workspaceId },
    {
      enabled: Boolean(deploymentId && workspaceId),
      initialData: initialStatus,
      meta: {
        toastError: (err: unknown) => {
          return `Failed to fetch deployment statuses: ${formatError(err)}`;
        },
      },
      refetchInterval: (data) => {
        const state = data?.state;
        return state === DeploymentStatus_DeploymentState.HEALTHY || state === DeploymentStatus_DeploymentState.FAILED
          ? 30_000
          : 5_000;
      },
    }
  );
  const deploymentStatus = deploymentStatusQuery.data;
  const stateProps = getStateProps(deploymentStatus?.state);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Tag title={stateProps.name} color={stateProps.color} />
      {showTooltip && deploymentStatus?.message && <Info isTooltip>{deploymentStatus?.message}</Info>}
    </div>
  );
};

const useColumns = ({
  onDeleteDeployment,
  onDownloadLogs,
  onEdit,
  onQuickScore,
  onResumeDeployment,
  onRetryDeployment,
  onPauseDeployment,
  permissions,
  isMlopsApiV1_0_x,
}: {
  onDeleteDeployment: (deploymentName: string) => void;
  onDownloadLogs: (deploymentName: string) => void;
  onEdit: (deploymentName: string) => void;
  onQuickScore: (deploymentName: string) => void;
  onResumeDeployment: (deployment: DeploymentFixed) => void;
  onRetryDeployment: (deploymentName: string) => void;
  onPauseDeployment: (deployment: DeploymentFixed) => void;
  permissions: GranularMLOpsPermissions;
  isMlopsApiV1_0_x: boolean;
}): IColumn[] => [
  {
    key: 'statusTag',
    name: '',
    fieldName: 'status',
    minWidth: 50,
    maxWidth: 50,
    data: {
      listCellProps: {
        styles: {
          root: {
            display: 'flex',
            justifyContent: 'center',
          },
        },
        onRenderText: ({ status }: DeploymentItem) => {
          const stateProps = getStateProps(status?.state);
          return (
            <StatusIndicator
              title={stateProps.name}
              color={stateProps.color}
              loading={
                status?.state === DeploymentStatus_DeploymentState.PENDING ||
                status?.state === DeploymentStatus_DeploymentState.PREPARING ||
                status?.state === DeploymentStatus_DeploymentState.LAUNCHING ||
                status?.state === DeploymentStatus_DeploymentState.TERMINATING
              }
            />
          );
        },
      },
    },
  },
  {
    key: 'title',
    name: 'Title',
    fieldName: 'description',
    minWidth: 300,
    maxWidth: 300,
    data: {
      headerFieldName: 'displayName',
      listCellProps: {
        onRenderHeader: ({ displayName, name }: DeploymentItem) =>
          RowHeaderTitle({
            title: displayName,
            onClick:
              permissions.isAbleToViewDeployment || permissions.isAbleToUpdateDeployments
                ? () => onEdit(name)
                : undefined,
          }),
        onRenderText: ({ createdByName, createTime }: DeploymentItem) => (
          <ListItemDescription createdBy={createdByName} createdAt={createTime || ``} />
        ),
        styles: {
          root: {
            paddingLeft: 12,
          },
        },
      },
    },
  },
  {
    key: 'type',
    name: 'Type',
    fieldName: 'type',
    minWidth: 100,
    maxWidth: 120,
  },
  {
    key: 'status',
    name: '',
    fieldName: 'status',
    minWidth: 170,
    maxWidth: 180,
    data: {
      listCellProps: {
        styles: {
          root: {
            justifyContent: 'center',
          },
        },
        onRenderText: ({ id, status, workspaceId }: DeploymentItem) => {
          return <DeploymentStatusWidget deploymentId={id} workspaceId={workspaceId} initialStatus={status} />;
        },
      },
    },
  },
  {
    key: 'buttons',
    name: '',
    minWidth: 130,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: isMlopsApiV1_0_x
          ? ({ name, status }: DeploymentItem) => (
              // TODO: Use theme prop for colors.
              <ContextMenuIconButton
                items={[
                  ...(permissions.isAbleToViewDeployment || permissions.isAbleToUpdateDeployments
                    ? [
                        {
                          key: permissions.isAbleToUpdateDeployments ? 'edit' : 'view',
                          text: permissions.isAbleToUpdateDeployments ? 'Edit' : 'View',
                          onClick: () => onEdit(name),
                          iconProps: {
                            iconName: permissions.isAbleToUpdateDeployments ? 'Edit' : 'RedEye',
                            style: { color: 'var(--h2o-gray900)' },
                          },
                        },
                      ]
                    : []),
                  ...(permissions.isAbleToViewDeployment && !!onDownloadLogs
                    ? [
                        {
                          key: 'downloadLogs',
                          text: 'Download logs',
                          onClick: () => onDownloadLogs(name),
                          style: {
                            color: 'var(--h2o-gray900)',
                          },
                          iconProps: { iconName: 'DownloadDocument', style: { color: 'var(--h2o-gray900)' } },
                        },
                      ]
                    : []),
                  ...(permissions.isAbleToQuickScoreDeployment &&
                  status.state === DeploymentStatus_DeploymentState.HEALTHY
                    ? [
                        {
                          key: 'quickScore',
                          text: 'Quick score',
                          onClick: () => onQuickScore(name),
                          style: {
                            color: 'var(--h2o-gray900)',
                          },
                          iconProps: { iconName: 'TableComputed', style: { color: 'var(--h2o-gray900)' } },
                        },
                      ]
                    : []),
                  ...(permissions.canRetryDeployments && status.state === DeploymentStatus_DeploymentState.FAILED
                    ? [
                        {
                          key: 'retryDeployment',
                          text: 'Retry deployment',
                          onClick: () => onRetryDeployment(name),
                          style: {
                            color: 'var(--h2o-gray900)',
                          },
                          iconProps: { iconName: 'Refresh', style: { color: 'var(--h2o-gray900)' } },
                        },
                      ]
                    : []),
                  ...(permissions.isAbleToDeleteDeployments
                    ? [
                        {
                          key: 'deleteDeployment',
                          text: 'Delete deployment',
                          onClick: () => onDeleteDeployment(name),
                          style: { color: 'var(--h2o-red400)' },
                          iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
                        },
                      ]
                    : []),
                ]}
              />
            )
          : (item: DeploymentItem) => {
              const {
                status: { state },
                name,
              } = item;
              const menuItems = [
                (permissions.isAbleToViewDeployment || permissions.isAbleToUpdateDeployments) && {
                  key: permissions.isAbleToUpdateDeployments ? 'edit' : 'view',
                  text: permissions.isAbleToUpdateDeployments ? 'Edit' : 'View',
                  onClick: () => onEdit(name),
                  iconProps: {
                    iconName: permissions.isAbleToUpdateDeployments ? 'Edit' : 'RedEye',
                    style: { color: 'var(--h2o-gray900)' },
                  },
                },
                permissions.isAbleToViewDeployment &&
                  !!onDownloadLogs && {
                    key: 'downloadLogs',
                    text: 'Download logs',
                    onClick: () => onDownloadLogs(name),
                    style: {
                      color: 'var(--h2o-gray900)',
                    },
                    iconProps: { iconName: 'DownloadDocument', style: { color: 'var(--h2o-gray900)' } },
                  },
                permissions.isAbleToQuickScoreDeployment &&
                  state === DeploymentStatus_DeploymentState.HEALTHY && {
                    key: 'quickScore',
                    text: 'Quick score',
                    onClick: () => onQuickScore(name),
                    style: {
                      color: 'var(--h2o-gray900)',
                    },
                    iconProps: { iconName: 'TableComputed', style: { color: 'var(--h2o-gray900)' } },
                  },
                permissions.isAbleToDeleteDeployments && {
                  key: 'deleteDeployment',
                  text: 'Delete deployment',
                  disabled: isMlopsApiV1_0_x
                    ? !(
                        item.status?.state === DeploymentStatus_DeploymentState.HEALTHY ||
                        item.status?.state === DeploymentStatus_DeploymentState.FAILED ||
                        item.status?.state === DeploymentStatus_DeploymentState.UNHEALTHY ||
                        item.status?.state === DeploymentStatus_DeploymentState.STOPPED
                      )
                    : !item.status.updatesAllowed,
                  onClick: () => onDeleteDeployment(name),
                  style: { color: 'var(--h2o-red400)' },
                  iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
                },
              ].filter(Boolean) as IContextualMenuItem[];

              switch (state) {
                case DeploymentStatus_DeploymentState.LAUNCHING:
                case DeploymentStatus_DeploymentState.PREPARING:
                case DeploymentStatus_DeploymentState.DEPLOYED:
                  return <ButtonStarting menuItems={menuItems} />;
                case DeploymentStatus_DeploymentState.FAILED:
                  return (
                    <ButtonFailed
                      menuItems={menuItems}
                      onClick={() => onRetryDeployment(name)}
                      primaryDisabled={!permissions.canRetryDeployments}
                    />
                  );
                case undefined:
                case DeploymentStatus_DeploymentState.DEPLOYMENT_STATE_UNSPECIFIED:
                case DeploymentStatus_DeploymentState.HEALTHY:
                case DeploymentStatus_DeploymentState.UNHEALTHY:
                  return (
                    <ButtonReady
                      menuItems={menuItems}
                      onClick={() => {
                        onPauseDeployment(item);
                      }}
                      primaryDisabled={!permissions.isAbleToPauseDeployments}
                    />
                  );
                case DeploymentStatus_DeploymentState.PENDING:
                  return <ButtonPausing menuItems={menuItems} text="Pending" />;
                case DeploymentStatus_DeploymentState.STOPPED:
                  return (
                    <ButtonPaused
                      menuItems={menuItems}
                      onClick={() => {
                        onResumeDeployment(item);
                      }}
                      primaryDisabled={!permissions.isAbleToResumeDeployments}
                    />
                  );
                case DeploymentStatus_DeploymentState.TERMINATING:
                  return <ButtonPausing menuItems={menuItems} />;
                case DeploymentStatus_DeploymentState.TERMINATED:
                  return <ButtonPaused menuItems={menuItems} primaryDisabled={true} />;
                default:
                  // exhaustive switch-case check
                  const s: never = state;
                  throw new Error(`Unknown deployment state: ${s}`);
              }
            },
        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
            marginRight: 9,
          },
        },
      },
    },
  },
];

const EndpointOptionsCopy = ({
  endpointOptions,
  defaultSelectedKey,
}: {
  endpointOptions: IDropdownOption[];
  defaultSelectedKey?: string;
}) => {
  const [internalSelectedKey, setInternalSelectedKey] = React.useState<string | undefined>();

  const effectiveKey = internalSelectedKey === undefined ? defaultSelectedKey : internalSelectedKey;

  React.useEffect(() => {
    setInternalSelectedKey(undefined);
  }, [defaultSelectedKey]);

  return (
    <TextWithCopy
      hasBorder
      header={
        <Dropdown
          options={endpointOptions}
          styles={dropdownStylesInlineLeft}
          onChange={(_, option) => setInternalSelectedKey(option?.key as string)}
          selectedKey={effectiveKey}
        />
      }
      styles={[
        textWithCopyStylesBorder,
        {
          header: {
            padding: 0,
            minWidth: 200,
            border: 0,
            borderRadius: 'none',
          },
          root: {
            width: '100%',
          },
        },
      ]}
      text={effectiveKey || 'No endpoint'}
    />
  );
};

const Deployments = () => {
  const params = useParams<{ workspace_id: string }>();
  const workspaceFQDN = React.useMemo(
    () => workspaceName.compile({ workspace: params.workspace_id }),
    [params.workspace_id]
  );
  const history = useHistory();
  const { addToast } = useToast();
  const permissions = useGranularMLOpsPermissions(workspaceFQDN);

  const [searchText, setSearchText] = React.useState<string>('');
  const deploymentsQuery = useQueryDeployments(
    {
      filter: searchText && `display_name = '*${searchText}*'`,
      orderBy: 'create_time desc',
      pageSize: 20,
      workspaceId: params.workspace_id,
    },
    {
      enabled: permissions.canListDeployments,
      meta: {
        toastError: (err: string) => `Failed to fetch deployments: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedDeployments = data.pages.flatMap((page) =>
          !page.deployments
            ? []
            : page.deployments.map((d) => {
                const { deployment: deploymentId, workspace: workspaceId } = deploymentName.parse(d.name);
                return { ...d, id: deploymentId, workspaceId };
              })
        );
        return { pages: flattenedDeployments, pageParams: data.pageParams };
      },
    }
  );
  const deployments = deploymentsQuery.data?.pages;
  const { usersNameMap } = useUsers();
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '';
  const queryClient = useQueryClient();
  const deploymentStatusesQuery = !deployments
    ? []
    : deployments.map((d) =>
        getDeploymentStatusQueryOptions({
          deploymentId: d.id,
          mlopsApiUrl,
          queryClient,
          workspaceId: d.workspaceId,
        })
      );
  const deploymentStatuses = useQueries({ queries: deploymentStatusesQuery }).map((query) => query.data);
  const deploymentMetadataQueryOptions = !deployments
    ? []
    : deployments.map((d) =>
        getDeploymentMetadataQueryOptions({
          deploymentId: d.id,
          mlopsApiUrl,
          queryClient,
          workspaceId: d.workspaceId,
        })
      );
  const deploymentMetadataQueries = useQueries({ queries: deploymentMetadataQueryOptions });
  const deploymentMetadata = deploymentMetadataQueries.map((query) => query.data);
  const endpointsQuery = useQueryEndpoints(
    {
      workspaceId: params.workspace_id,
      pageSize: 999,
    },
    {
      enabled: Boolean(params.workspace_id && permissions?.isAbleToViewEndpoints),
      meta: {
        toastError: (err: unknown) => `Failed to fetch endpoints: ${formatError(err)}`,
      },
      select: (data) => {
        const flattenedEndpoints = data.pages.flatMap((page) => page.endpoints || []);
        return { pages: flattenedEndpoints, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(endpointsQuery);
  const endpoints = endpointsQuery.data?.pages;
  const deploymentItems = React.useMemo<DeploymentItem[] | undefined>(() => {
    // @ts-expect-error Object.groupBy is not available in TypeScript < 5.2
    const endpointsByDeploymentName = !endpoints ? {} : Object.groupBy(endpoints, (e) => e.target);
    return deployments?.map((deployment, idx) => {
      const isLoading = deploymentMetadataQueries.some((query) => query.isLoading) || endpointsQuery.isLoading;
      const status = deploymentStatuses[idx];
      const metadata = deploymentMetadata[idx];
      const endpoint = metadata?.scoringInterface?.score;
      const endpointsForDeployment = endpointsByDeploymentName[deployment.name];
      const { deployment: deploymentId } = deploymentName.parse(deployment.name);
      const defaultKey = endpoint?.uri;
      const [urlPrefix, urlSuffix] = defaultKey?.includes(deploymentId) ? defaultKey.split(deploymentId, 2) : ['', ''];
      const endpointOptions: IDropdownOption[] = [];
      if (defaultKey) {
        endpointOptions.push({ key: defaultKey, text: 'Scorer (default)' });
      }
      if (endpointsForDeployment) {
        endpointOptions.push(
          // TODO: Replace "any" type.
          ...endpointsForDeployment.map((endpoint: any) => ({
            key: `${urlPrefix}${endpoint.path}${urlSuffix}`,
            text: endpoint.displayName || `${urlPrefix}${endpoint.path}${urlSuffix}`,
          }))
        );
      }

      return {
        ...deployment,
        createdByName: deployment.creator ? usersNameMap?.[deployment?.creator]?.displayName || deployment.creator : '',
        status: status || { state: DeploymentStatus_DeploymentState.DEPLOYMENT_STATE_UNSPECIFIED },
        type: getDeploymentTypeDisplayName(deployment),
        contents: (
          <div
            style={{
              marginBottom: 16,
            }}
          >
            <h4 style={{ margin: 0, marginBottom: 6 }}>Endpoints</h4>
            {endpointOptions.length ? (
              isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
                  <Loader />
                </div>
              ) : (
                <EndpointOptionsCopy
                  endpointOptions={endpointOptions}
                  defaultSelectedKey={defaultKey || (endpointOptions[0]?.key as string)}
                />
              )
            ) : (
              <div style={{ minHeight: 32, display: 'flex', alignContent: 'center', flexWrap: 'wrap' }}>
                No endpoints available
              </div>
            )}
          </div>
        ),
      };
    });
  }, [deployments, deploymentMetadata, deploymentStatuses, endpoints, usersNameMap]);

  const deleteDeploymentMutation = useMutationDeleteDeployment({
    onError: (err) => {
      const message = `Failed to delete deployment: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
  });

  const retryDeploymentMutation = useMutationRetryDeployment({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to retry deployment: ${formatError(err)}`,
      });
    },
  });

  const { handlePauseDeployment, handleResumeDeployment } = usePauseResumeDeployment();

  const loading = deploymentsQuery.isLoading;

  const theme = useTheme(),
    env = useEnv(),
    classNames = useClassNames<IDeploymentsStyles, ClassNamesFromIStyles<IDeploymentsStyles>>(
      'deploymentsStyles',
      deploymentsStyles(theme)
    ),
    { showDialog } = useConfirmDialog(),
    mlopsService = useMLOpsService(),
    [isDialogVisible, setIsDialogVisible] = React.useState(false),
    dialogTimeoutRef = React.useRef<NodeJS.Timeout | null>(null),
    onAction = () => history.push(`/mlops/${workspaceFQDN}${ROUTES.DEPLOYMENTS}/create-new`),
    breadcrumbs: IBreadcrumbItem[] = [
      {
        key: `home`,
        text: `home`,
        onClick: () => history.push('/'),
      },
      {
        key: `mlops`,
        text: `MLOps`,
        onClick: () => history.push('/mlops'),
      },
      {
        key: 'deployments',
        text: 'Deployments',
        isCurrentItem: true,
      },
    ],
    generateDeploymentLogs = async (deploymentId: string) => {
      try {
        const response = await mlopsService.generateDeploymentLogs({
          name: deploymentId,
        });
        const logZipArtifactId = response?.artifactId;
        if (!logZipArtifactId) console.error('No log entries found in the response.');
        return logZipArtifactId;
      } catch (err) {
        const message = `Failed to fetch log entries: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
    },
    onDismissDialog = () => {
      setIsDialogVisible(false);
      if (dialogTimeoutRef.current) {
        clearTimeout(dialogTimeoutRef.current);
        dialogTimeoutRef.current = null;
      }
    },
    onDownloadLogs = React.useCallback(
      async (deploymentName: string) => {
        if (!deploymentName) return;
        try {
          dialogTimeoutRef.current = setTimeout(() => {
            setIsDialogVisible(true);
            dialogTimeoutRef.current = null;
          }, 1500);
          const logZipArtifactId = await generateDeploymentLogs(deploymentName);
          if (!logZipArtifactId) return;
          const fileName = `deployment-${deploymentName.replace('/', '_')}-logs.zip`;
          await downloadFileFromGoogleByteStream(logZipArtifactId, mlopsApiUrl, fileName);
        } catch (err) {
          const message = `Failed to download logs: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          onDismissDialog();
        }
      },
      [mlopsApiUrl]
    ),
    headerActionProps = React.useMemo(
      () =>
        permissions.isAbleToCreateDeployments
          ? {
              actionTitle: 'Create deployment',
              onActionClick: onAction,
              actionIcon: 'Add',
            }
          : undefined,
      [permissions.isAbleToCreateDeployments, onAction]
    ),
    mlopsApiVersionQuery = useQueryMlopsApiVersion(),
    mlopsApiVersion = mlopsApiVersionQuery.data,
    isMlopsApiV1_0_x = mlopsApiVersion?.startsWith('1.0.'),
    columns = useColumns({
      onDeleteDeployment: (deploymentName) => {
        showDialog?.({
          title: 'Delete deployment?',
          onConfirm: () => {
            void deleteDeploymentMutation.mutate({ name: deploymentName });
          },
        });
      },
      onDownloadLogs: (deploymentName) => onDownloadLogs(deploymentName),
      onEdit: (deploymentName) => history.push(`/mlops/${deploymentName}`),
      onQuickScore: (deploymentName) => history.push(`/mlops/${deploymentName}/scoring`),
      onResumeDeployment: handleResumeDeployment,
      onRetryDeployment: (deploymentName) => {
        retryDeploymentMutation.mutate({ name: deploymentName });
      },
      onPauseDeployment: handlePauseDeployment,
      permissions,
      isMlopsApiV1_0_x: Boolean(isMlopsApiV1_0_x),
    }),
    widgetListProps = {
      columns: columns,
      items: deploymentItems,
      loading,
      delayLoader: false,
      isLoadingMore: deploymentsQuery.isFetchingNextPage,
      onLoadMore: deploymentsQuery.hasNextPage ? deploymentsQuery.fetchNextPage : undefined,
      isLoadingSearch: Boolean(searchText && deploymentsQuery.isLoading),
      lastRefreshed: !deploymentsQuery.dataUpdatedAt
        ? undefined
        : formatDefaultDateAndTime(deploymentsQuery.dataUpdatedAt),
      onRefresh: deploymentsQuery.refetch,
      searchProps: {
        placeholder: 'Search deployments',
        // TODO: Check if "value" is correct.
        onSearchChange: setSearchText,
        searchText,
      },
      actionProps: headerActionProps,
      NoItemsContent: NoItemView({
        title: 'No deployments',
        description: 'There are no deployments available in this workspace. Deploy your models first.',
        bannerWidth: MLOPS_NO_ITEMS_BANNER_WIDTH,
        shadowed: true,
        ...headerActionProps,
      }),
      ErrorContent: FailedToLoadView({
        title: 'Failed to load deployments',
        description: 'Please try again later. If the problem persists, contact our support.',
        actionTitle: 'Retry',
        onActionClick: deploymentsQuery.refetch,
        actionIcon: 'Refresh',
      }),
      contentStyles: {
        paddingLeft: 62,
        paddingRight: 32,
      },
    };

  useHaicPageTitle('Deployments | MLOps', env?.cloudInstanceName);

  return (
    <>
      <BaseDialog
        hidden={!isDialogVisible}
        dialogContentProps={{
          type: DialogType.close,
        }}
        modalProps={
          {
            // HACK: Pass empty modalProps, otherwise download button won't open system file save dialog.
          }
        }
        content={
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <p>Preparing log files, please wait...</p>
          </div>
        }
        onDismiss={onDismissDialog}
      />

      <NavigationWrapper>
        <PageWrapper>
          {deploymentItems?.length || searchText || loading ? (
            <HeaderPure items={breadcrumbs} pageTitle="Deployments" />
          ) : null}
          {permissions.isAbleToViewDeployments ? (
            <WidgetList {...widgetListProps} />
          ) : (
            <div className={classNames.noPermissionsInfo}>You do not have permission to view deployments.</div>
          )}
        </PageWrapper>
      </NavigationWrapper>
    </>
  );
};

export default Deployments;
